import { useState, useRef } from 'react'
import { notification } from 'antd'
import FileSaver from 'file-saver'
import { QRCodeData, QrCodeFormData, QrCodeState } from 'Types/QrCode.d'
import {
  processDataInChunks,
  extractSheetInfo,
  parseCsvData,
  validateSheetHeaders,
  findSheetColumns,
  addUtmParameters,
  shortenUrl,
  loadImage,
  generateQRWithLogo,
  dataURLtoBlob,
  createCsvContent
} from 'Utils/qrCodeUtils'
import OLLogo from 'Assets/images/onelife_logo.png'

const DEFAULT_FORM_DATA: QrCodeFormData = {
  google_sheet_url: '',
  tabName: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  logoUrl: '',
  useDefaultLogo: true,
  logoSizePercent: 15,
  errorCorrectionLevel: 'L',
  qrWidth: 2400,
  kuttApiKey: 'TVs7WekBKVkR72xPGDKg7XwGLFRBQDck6psq_scy',
  kuttDomain: 'https://kutt.onelife.vn',
  useKutt: false
}

const DEFAULT_STATE: QrCodeState = {
  loading: false,
  generating: false,
  shorteningUrls: false,
  sheetData: [],
  qrCodeData: [],
  columns: [],
  error: '',
  loadingProgress: 0,
  showProgress: false,
  exportUrl: '',
  previewVisible: false,
  previewImage: '',
  previewTitle: '',
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number) => `Total ${total} items`,
  }
}

export const useQrCodeGenerator = () => {
  const [formData, setFormData] = useState<QrCodeFormData>(DEFAULT_FORM_DATA)
  const [state, setState] = useState<QrCodeState>(DEFAULT_STATE)
  const qrCodeContainerRef = useRef<HTMLDivElement>(null)

  const updateFormData = (updates: Partial<QrCodeFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }

  const updateState = (updates: Partial<QrCodeState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }

  const fetchSheetData = async () => {
    if (!formData.google_sheet_url) return

    updateState({ 
      loading: true, 
      error: '', 
      showProgress: true, 
      loadingProgress: 0 
    })

    try {
      const { csvUrl } = extractSheetInfo(formData.google_sheet_url)
      
      notification.info({
        message: 'Fetching data',
        description: 'Starting to download sheet data. This may take a while for large sheets.',
        duration: 3
      })
      
      console.log('Fetching from URL:', csvUrl)
      
      const response = await fetch(csvUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}. Make sure the sheet is shared publicly with "Anyone with the link can view" access.`)
      }
      
      notification.info({
        message: 'Processing data',
        description: 'Sheet data downloaded. Now processing the data...',
        duration: 3
      })
      
      const csvText = await response.text()
      console.log('CSV first 200 chars:', csvText.substring(0, 200))
      
      const rows = parseCsvData(csvText)
      console.log('First row (headers):', rows[0])
      console.log('Second row (sample data):', rows[1])
      console.log('Total rows:', rows.length)
      
      // Validate CSV structure
      if (rows.length < 2) {
        throw new Error('CSV file must contain at least a header row and one data row.')
      }
      
      const headerCount = rows[0]?.length || 0
      if (headerCount === 0) {
        throw new Error('CSV file appears to have no columns. Please check the file format.')
      }
      
      // Check for inconsistent column counts (could indicate parsing issues)
      const inconsistentRows = rows.slice(1).filter(row => row.length !== headerCount)
      if (inconsistentRows.length > 0) {
        console.warn(`Found ${inconsistentRows.length} rows with inconsistent column counts. This might indicate special characters in the data.`)
        console.warn('Sample inconsistent rows:', inconsistentRows.slice(0, 3))
      }
      
      if (rows.length > 0) {
        const headers = rows[0]
        console.log('Headers found:', headers)
        
        validateSheetHeaders(headers)

        // Process data in chunks to prevent UI freezing
        const data = await processDataInChunks(rows.slice(1), headers, (progress) => {
          updateState({ loadingProgress: progress })
        })

        const columns = headers.map((header: string) => ({
          title: header,
          dataIndex: header,
          key: header,
          ellipsis: true,
          width: 200,
        }))

        updateState({
          columns,
          sheetData: data,
          pagination: {
            ...state.pagination,
            total: data.length
          }
        })
        
        notification.success({
          message: 'Data loaded successfully',
          description: `Processed ${data.length} rows of data`,
          duration: 3
        })
      }
    } catch (error: any) {
      console.error('Error fetching sheet data:', error)
      const errorMessage = error.message || 'Failed to fetch Google Sheet data. Please make sure the sheet is publicly accessible.'
      updateState({ 
        error: errorMessage,
        sheetData: [],
        columns: []
      })
    } finally {
      updateState({ 
        loading: false, 
        showProgress: false 
      })
    }
  }

  const generateQRCodes = async () => {
    if (!state.sheetData.length) {
      notification.error({
        message: 'No data',
        description: 'Please load Google Sheet data first',
      })
      return
    }

    updateState({
      generating: true,
      qrCodeData: [],
      showProgress: true,
      loadingProgress: 0
    })

    try {
      const { urlColumn, skuColumn, nameColumn } = findSheetColumns(state.sheetData)
      
      if (!urlColumn || !skuColumn) {
        throw new Error('Required columns not found in the sheet data')
      }

      // Preload logo if provided
      let logoImage: HTMLImageElement | null = null
      
      if (formData.useDefaultLogo) {
        logoImage = await loadImage(OLLogo)
        if (!logoImage) {
          notification.warning({
            message: 'Default Logo Warning',
            description: 'Could not load the default logo. QR codes will be generated without logo.'
          })
        }
      } else if (formData.logoUrl) {
        logoImage = await loadImage(formData.logoUrl)
        if (!logoImage) {
          notification.warning({
            message: 'Logo Warning',
            description: 'Could not load the logo image. QR codes will be generated without logo.'
          })
        }
      }

      const processedData: QRCodeData[] = []
      
      // Show URL shortening notification if using Kutt
      if (formData.useKutt && formData.kuttApiKey) {
        updateState({ shorteningUrls: true })
        notification.info({
          message: 'Shortening URLs',
          description: `Using Kutt to shorten URLs before generating QR codes. This may take some time.`,
          duration: 4
        })
      }
      
      for (let i = 0; i < state.sheetData.length; i++) {
        const row = state.sheetData[i]
        const baseUrl = row[urlColumn]
        const sku = row[skuColumn]
        const productName = nameColumn ? row[nameColumn] : ''
        
        if (!baseUrl || !sku) continue
        
        // Add UTM parameters to the URL
        let finalUrl = addUtmParameters(
          baseUrl, 
          formData.utmSource, 
          formData.utmMedium, 
          formData.utmCampaign
        )
        
        // Shorten URL if using Kutt
        if (formData.useKutt && formData.kuttApiKey) {
          try {
            finalUrl = await shortenUrl(finalUrl, productName, formData.kuttApiKey, formData.kuttDomain)
          } catch (error: any) {
            notification.error({
              message: 'URL Shortening Failed',
              description: `Could not shorten URL: ${error.message}. Using original URL instead.`,
              duration: 3
            })
          }
        }
        
        // Generate QR codes with high quality settings (black and white)
        const qrCodeWhite = await generateQRWithLogo(finalUrl, {
          color: {
            dark: '#000000',
            light: ''
          },
          width: formData.qrWidth,
          margin: 1,
          logoImage,
          logoWidth: 0,
          logoHeight: 0
        }, formData.logoSizePercent)
        
        const qrCodeBlack = await generateQRWithLogo(finalUrl, {
          color: {
            dark: '#FFFFFF',
            light: '#red'
          },
          width: formData.qrWidth,
          margin: 1,
          logoImage,
          logoWidth: 0,
          logoHeight: 0
        }, formData.logoSizePercent)
        
        processedData.push({
          key: i,
          product_url: finalUrl,
          sku: sku,
          product_name: productName,
          qr_code_white: qrCodeWhite,
          qr_code_black: qrCodeBlack
        })
        
        // Update progress
        updateState({ loadingProgress: Math.floor(((i + 1) / state.sheetData.length) * 100) })
      }
      
      // Export data to CSV
      const csvContent = createCsvContent(processedData)
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      
      updateState({
        qrCodeData: processedData,
        exportUrl: url,
        shorteningUrls: false
      })
      
      notification.success({
        message: 'QR codes generated',
        description: `Generated ${processedData.length} QR codes successfully with print-quality resolution (${formData.qrWidth}x${formData.qrWidth}px)${formData.useKutt ? ' and shortened URLs' : ''}`,
      })
    } catch (error: any) {
      console.error('Error generating QR codes:', error)
      notification.error({
        message: 'Error',
        description: `Failed to generate QR codes: ${error.message || 'Unknown error'}`,
      })
    } finally {
      updateState({ 
        generating: false, 
        shorteningUrls: false, 
        showProgress: false 
      })
    }
  }

  const handleDownloadQRCodes = () => {
    // Download all QR codes
    state.qrCodeData.forEach((item) => {
      // Download white on transparent QR code
      if (item.qr_code_black) {
        const blobBlack = dataURLtoBlob(item.qr_code_black)
        FileSaver.saveAs(blobBlack, `${item.sku}_white_on_transparent.png`)
      }
      
      // Download black on transparent QR code
      if (item.qr_code_white) {
        const blobWhite = dataURLtoBlob(item.qr_code_white)
        FileSaver.saveAs(blobWhite, `${item.sku}_black_on_transparent.png`)
      }
    })
    
    notification.success({
      message: 'Download started',
      description: `QR codes are being downloaded in print-quality PNG format (${formData.qrWidth}x${formData.qrWidth}px) with transparent background${formData.useDefaultLogo || formData.logoUrl ? ' and logo.' : '.'}Both black and white QR code versions are included.`,
      duration: 8
    })
  }

  const handleTableChange = (newPagination: any) => {
    updateState({
      pagination: {
        ...state.pagination,
        ...newPagination
      }
    })
  }

  const handlePreview = (image: string, title: string) => {
    updateState({
      previewImage: image,
      previewTitle: title,
      previewVisible: true
    })
  }

  const closePreview = () => {
    updateState({ previewVisible: false })
  }

  return {
    // State
    formData,
    state,
    qrCodeContainerRef,
    
    // Actions
    updateFormData,
    updateState,
    fetchSheetData,
    generateQRCodes,
    handleDownloadQRCodes,
    handleTableChange,
    handlePreview,
    closePreview
  }
} 